import { PerspectiveCamera, Vector3 as ThreeVector3 } from "three";
import type { Vector3 } from "../simulation/types";

export const DEFAULT_CAMERA_POSITION: Vector3 = [13, 9, 15];
export const DEFAULT_CAMERA_VERTICAL_FOV_DEGREES = 44;
export const SUN_POSITION: Vector3 = [-20, 0, 0];
export const SUN_RADIUS = 4.2;

// Keep the whole Sun disc inside the frame with a small margin at the left edge.
const SUN_FAR_EDGE_NDC_X = -0.94;
// Whole-disc placement must not push the Sun this far into the frame.
const SUN_NEAR_EDGE_MAX_NDC_X = -0.2;
// On narrow aspects the whole disc cannot fit; anchor its near edge here instead.
const SUN_NEAR_EDGE_NDC_X_NARROW = -0.55;
const TARGET_SOLVER_ITERATIONS = 48;
const WORLD_UP = new ThreeVector3(0, 1, 0);

interface SunEdges {
  far: number;
  near: number;
}

function projectedSunEdges(
  camera: PerspectiveCamera,
  targetX: number,
): SunEdges {
  camera.lookAt(targetX, 0, 0);
  camera.updateMatrixWorld(true);

  const cameraSpaceSun = new ThreeVector3(...SUN_POSITION).applyMatrix4(
    camera.matrixWorldInverse,
  );
  const centreAngle = Math.atan2(cameraSpaceSun.x, -cameraSpaceSun.z);
  const angularRadius = Math.asin(
    SUN_RADIUS / Math.hypot(cameraSpaceSun.x, cameraSpaceSun.z),
  );
  const horizontalHalfFieldOfView = Math.atan(
    Math.tan((DEFAULT_CAMERA_VERTICAL_FOV_DEGREES * Math.PI) / 360) *
      camera.aspect,
  );

  return {
    far: (centreAngle - angularRadius) / horizontalHalfFieldOfView,
    near: (centreAngle + angularRadius) / horizontalHalfFieldOfView,
  };
}

function solveTargetX(
  camera: PerspectiveCamera,
  pickEdge: (edges: SunEdges) => number,
  goalNdcX: number,
): number {
  let leftTargetX = SUN_POSITION[0];
  let rightTargetX = DEFAULT_CAMERA_POSITION[0];

  for (
    let iteration = 0;
    iteration < TARGET_SOLVER_ITERATIONS;
    iteration += 1
  ) {
    const targetX = (leftTargetX + rightTargetX) / 2;
    const edge = pickEdge(projectedSunEdges(camera, targetX));

    if (edge > goalNdcX) {
      leftTargetX = targetX;
    } else {
      rightTargetX = targetX;
    }
  }

  return (leftTargetX + rightTargetX) / 2;
}

export function getDefaultCameraTarget(aspect: number): Vector3 {
  if (!Number.isFinite(aspect) || aspect <= 0) {
    throw new RangeError("Camera aspect must be finite and positive");
  }

  const camera = new PerspectiveCamera(
    DEFAULT_CAMERA_VERTICAL_FOV_DEGREES,
    aspect,
    0.1,
    100,
  );
  camera.position.set(...DEFAULT_CAMERA_POSITION);
  camera.up.copy(WORLD_UP);

  const wholeDiscX = solveTargetX(
    camera,
    (edges) => edges.far,
    SUN_FAR_EDGE_NDC_X,
  );

  if (projectedSunEdges(camera, wholeDiscX).near < SUN_NEAR_EDGE_MAX_NDC_X) {
    return [wholeDiscX, 0, 0];
  }

  // Narrow frame: show a clear partial disc instead of squeezing the scene.
  const partialDiscX = solveTargetX(
    camera,
    (edges) => edges.near,
    SUN_NEAR_EDGE_NDC_X_NARROW,
  );

  return [partialDiscX, 0, 0];
}
